import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../constants/config";
import { Smartphone, Save, Loader2, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import AppVersionHistory from "./AppVersionHistory";

const defaultForm = {
  minimumRequiredVersion: "",
  latestVersion: "",
  forceUpdateMessage: "",
  showUpdatePrompt: true,
  isForced: false,
  changeReason: "",
};

function AppVersionConfig() {
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [activeTab, setActiveTab] = useState("config");

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(API_ENDPOINTS.appConfig);
      const cfg = response.data?.data?.appVersionConfig ?? defaultForm;
      setForm({
        minimumRequiredVersion: cfg.minimumRequiredVersion ?? "",
        latestVersion: cfg.latestVersion ?? "",
        forceUpdateMessage: cfg.forceUpdateMessage ?? "",
        showUpdatePrompt: cfg.showUpdatePrompt !== false,
        isForced: cfg.isForced === true,
        changeReason: "",
      });
    } catch (err) {
      setMessage({ type: "error", text: "Failed to load app version config" });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setMessage({ type: "", text: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setMessage({ type: "", text: "" });
      const token = localStorage.getItem("token");
      await axiosInstance.patch(
        API_ENDPOINTS.appConfig,
        {
          minimumRequiredVersion: form.minimumRequiredVersion.trim() || null,
          latestVersion: form.latestVersion.trim() || null,
          forceUpdateMessage: form.forceUpdateMessage.trim() || null,
          showUpdatePrompt: form.showUpdatePrompt,
          isForced: form.isForced,
          changeReason: form.changeReason.trim() || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage({ type: "success", text: "App version config saved successfully" });
      setForm((prev) => ({ ...prev, changeReason: "" }));
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to save config";
      setMessage({ type: "error", text: msg });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Smartphone className="w-7 h-7 text-blue-600" />
          <h2 className="text-2xl font-bold">App Version Config</h2>
        </div>
        <div className="flex gap-2 border border-gray-300 rounded-lg p-1 bg-white">
          <button
            type="button"
            onClick={() => setActiveTab("config")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === "config"
                ? "bg-blue-600 text-white"
                : "text-gray-700 hover:bg-gray-100"
            }`}
          >
            <Smartphone className="w-4 h-4 inline mr-2" />
            Configuration
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("history")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === "history"
                ? "bg-blue-600 text-white"
                : "text-gray-700 hover:bg-gray-100"
            }`}
          >
            <History className="w-4 h-4 inline mr-2" />
            History
          </button>
        </div>
      </div>

      {activeTab === "config" && (
        <>
          <p className="text-gray-600 text-sm">
            Control force-update behaviour for the mobile app. Leave blank to use store-based version check.
          </p>

          {message.text && (
            <div
              className={`px-4 py-3 rounded-lg ${
                message.type === "error"
                  ? "bg-red-100 border border-red-400 text-red-700"
                  : "bg-green-100 border border-green-400 text-green-700"
              }`}
              role="alert"
            >
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md space-y-5 max-w-xl">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Minimum required version (e.g. 1.2.0)
          </label>
          <input
            type="text"
            value={form.minimumRequiredVersion}
            onChange={(e) => handleChange("minimumRequiredVersion", e.target.value)}
            placeholder="1.0.0"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Latest version (optional)
          </label>
          <input
            type="text"
            value={form.latestVersion}
            onChange={(e) => handleChange("latestVersion", e.target.value)}
            placeholder="1.2.0"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Force update message (optional)
          </label>
          <textarea
            value={form.forceUpdateMessage}
            onChange={(e) => handleChange("forceUpdateMessage", e.target.value)}
            placeholder="A new version is required. Please update to continue."
            rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="showUpdatePrompt"
            checked={form.showUpdatePrompt}
            onChange={(e) => handleChange("showUpdatePrompt", e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="showUpdatePrompt" className="text-sm font-medium text-gray-700">
            Show update prompt to users
          </label>
        </div>
        <div className={`p-4 rounded-lg border-2 ${
          form.isForced
            ? "bg-amber-50 border-amber-300"
            : "bg-gray-50 border-gray-200"
        }`}>
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="isForced"
              checked={form.isForced}
              onChange={(e) => handleChange("isForced", e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500 mt-0.5"
            />
            <div className="flex-1">
              <label htmlFor="isForced" className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                Force Update (Non-dismissable)
                {form.isForced && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-200 text-amber-800">
                    ⚠️ ACTIVE
                  </span>
                )}
              </label>
              <p className="text-xs text-gray-600 mt-1">
                When enabled, users cannot dismiss the update and must update to continue using the app.
                {form.isForced && (
                  <span className="block mt-1 font-medium text-amber-700">
                    ⚠️ Use only for critical security updates or breaking changes.
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Change Reason (Optional)
          </label>
          <textarea
            value={form.changeReason}
            onChange={(e) => handleChange("changeReason", e.target.value)}
            placeholder="Explain why you're making this change (e.g., 'Critical security patch required')"
            rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <small className="text-xs text-gray-600 mt-1 block">
            This will be logged in the history for audit purposes
          </small>
        </div>
        <Button type="submit" disabled={saving} className="flex items-center gap-2">
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {saving ? "Saving..." : "Save"}
        </Button>
          </form>
        </>
      )}

      {activeTab === "history" && (
        <AppVersionHistory />
      )}
    </div>
  );
}

export default AppVersionConfig;
